import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import * as API from '../apiConfig';

function OrdersComponent({ authData }) {
    const { orderId } = useParams();

    const [order, setOrder] = useState(null);
    const [stage, setStage] = useState('');
    const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
    const [notes, setNotes] = useState([]);
    const [noteInput, setNoteInput] = useState('');
    const [isLoadingNotes, setIsLoadingNotes] = useState(false);
    const [isAddingNote, setIsAddingNote] = useState(false);
    const [notesError, setNotesError] = useState('');

    const [isEditing, setIsEditing] = useState(false);
    const [editedRate, setEditedRate] = useState('');
    const [editedAmount, setEditedAmount] = useState('');
    const [saving, setSaving] = useState(false);
    const [isEditingAmount, setIsEditingAmount] = useState(false);

    const stages = [
        "Processing",
        "Cutting",
        "Polishing",
        "Glass Cutting With Frosted",
        "Dispatched for Coloring",
        "Returned from Coloring",
        "Completed",
        "Delivered via Porter",
        "Client Picked Up"

    ];

    const previewAmount =
        isEditingAmount
            ? parseFloat(editedAmount || 0)
            : (editedRate && order?.totalSquareFeet
                ? parseFloat(editedRate) * order.totalSquareFeet
                : order?.amount);

    const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authData?.token}`
    };

    useEffect(() => {
        fetch(`${API.ORDERS_API_URL}/${orderId}`, { headers })
            .then(res => res.json())
            .then(data => {
                setOrder(data.order);
                setStage(data.order.currentStage);
                setEditedRate(data.order.rate);
                setEditedAmount(data.order.amount);
            })
            .catch(console.error);
    }, [orderId]);

    const fetchNotes = async () => {
        setIsLoadingNotes(true);
        setNotesError('');

        try {
            const response = await fetch(`${API.NOTES_URL}/order/${orderId}`, {
                headers
            });
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Unable to load notes');
            }

            setNotes(Array.isArray(data.data) ? data.data : []);
        } catch (error) {
            setNotesError(error.message || 'Unable to load notes');
        } finally {
            setIsLoadingNotes(false);
        }
    };

    const openNotesModal = async () => {
        setIsNotesModalOpen(true);
        await fetchNotes();
    };

    const handleAddNote = async () => {
        const trimmedNote = noteInput.trim();
        if (!trimmedNote) {
            return;
        }

        setIsAddingNote(true);
        setNotesError('');

        try {
            const response = await fetch(`${API.NOTES_URL}/create`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    orderId: Number(orderId),
                    note: trimmedNote
                })
            });
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Unable to add note');
            }

            setNoteInput('');
            await fetchNotes();
        } catch (error) {
            setNotesError(error.message || 'Unable to add note');
        } finally {
            setIsAddingNote(false);
        }
    };

    const formatNoteTime = (value) => {
        if (!value) {
            return '-';
        }
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }
        return date.toLocaleString();
    };

    const updateOrderDetails = async () => {
        setSaving(true);

        try {
            const rate = parseFloat(editedRate) || 0;

            // derive amount from rate
            const totalSqFt = parseFloat(order.totalSquareFeet) || 0;
            const amount = rate * totalSqFt;

            const payload = {
                rate,
                amount
            };

            const res = await fetch(`${API.ORDERS_API_URL}/${orderId}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(payload)
            });

            const data = await res.json();

            if (res.ok) {
                setOrder(data.order);

                // sync states from backend
                setEditedRate(data.order.rate);
                setEditedAmount(data.order.amount);

                setIsEditing(false);
                setIsEditingAmount(false);

            } else {
                alert(data.error || "Failed to update");
            }

        } catch (err) {
            alert("Error updating order");
        } finally {
            setSaving(false);
        }
    };

    const updateStage = async () => {

        let finalStage = stage;
        let deliveryMode = null;

        // ✅ Handle delivery cases
        if (
            stage === "Delivered via Porter" ||
            stage === "Client Picked Up"
        ) {
            const confirm = window.confirm(
                `Are you sure you want to mark as "${stage}"?`
            );

            if (!confirm) return;

            // 👇 normalize stage
            finalStage = "Delivered";

            // 👇 set delivery mode
            deliveryMode =
                stage === "Delivered via Porter"
                    ? "Delivered via Porter"
                    : "Client Picked Up";
        }

        // ✅ Build URL
        let url = `${API.ORDERS_API_URL}/${orderId}/stage?stage=${finalStage}`;

        if (deliveryMode) {
            url += `&mode=${deliveryMode}`;
        }

        const res = await fetch(url, {
            method: 'PUT',
            headers
        });

        if (res.ok) {
            const updated = await fetch(`${API.ORDERS_API_URL}/${orderId}`, { headers });
            const data = await updated.json();
            setOrder(data.order);

            alert("Updated the order status to " + finalStage);
        }
    };

    if (!order)
        return (
            <div className="d-flex justify-content-center py-5">
                <div className="spinner-border"></div>
            </div>
        );

    return (
        <div className="container py-3 px-3 px-sm-0" style={{ maxWidth: "720px" }}>

            {/* Header */}
            <div className="mb-4 d-flex justify-content-between align-items-start gap-2">
                <div>
                    <h5 className="fw-semibold mb-1">
                        Order #{order.orderId}
                    </h5>
                    <div className="text-muted small">
                        {order.clientName}
                    </div>
                </div>

            </div>

            {/* Image */}
            <div className="mb-4 text-center">
                <img
                    src={order.imagePath}
                    alt="order"
                    className="img-fluid rounded-3 shadow-sm"
                    style={{
                        maxHeight: "200px",
                        objectFit: "contain",
                        width: "100%"
                    }}
                />
            </div>

            {/* Info Card */}
            <div className="card border-0 shadow-sm mb-4 rounded-4">
                <div className="card-body p-3 p-sm-4">

                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <button
                            type="button"
                            className="btn btn-outline-danger fs-1 btn-sm"
                            onClick={openNotesModal}
                        >
                            Notes
                        </button>
                        <span className="text-muted small">Status</span>
                        <span className="badge bg-dark px-3 py-2">
                            {order.currentStage}
                        </span>
                    </div>

                    {[
                        ["Size", `${order.width} × ${order.height}`],
                        ["Sq Ft", order.totalSquareFeet],
                        ["Quantity", order.quantity],
                    ].map(([label, value], i) => (
                        <div key={i} className="d-flex justify-content-between py-2 border-bottom">
                            <span className="text-muted small">{label}</span>
                            <span className="fw-medium">{value}</span>
                        </div>
                    ))}
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                        <span className="text-muted small">Rate</span>

                        {!isEditing ? (
                            <div className="d-flex align-items-center gap-2">
                                <span className="fw-medium">₹{order.rate}</span>

                                {!order.billed && (
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => {
                                            setIsEditing(true);
                                            setIsEditingAmount(false);   // ✅ reset amount editing
                                            setEditedAmount(order.amount); // optional: reset value
                                        }}
                                    >
                                        Edit
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="d-flex gap-2">
                                <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    style={{ width: "90px" }}
                                    value={editedRate}
                                    onChange={(e) => setEditedRate(e.target.value)}
                                />

                                <button
                                    className="btn btn-sm btn-success"
                                    onClick={updateOrderDetails}
                                    disabled={saving}
                                >
                                    {saving ? "..." : "Save"}
                                </button>

                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => {
                                        setIsEditing(false);
                                        setEditedRate(order.rate);
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>


                    {/* <div className="d-flex justify-content-between py-2">
                        <span className="text-muted small">Amount</span>
                        <span className="fw-semibold">
                            ₹{isEditing ? previewAmount : order.amount}
                        </span>
                    </div> */}

                    <div className="d-flex justify-content-between align-items-center py-2">
                        <span className="text-muted small">Amount</span>

                        {!isEditingAmount ? (
                            <div className="d-flex align-items-center gap-2">
                                <span className="fw-semibold">
                                    ₹{isEditing ? previewAmount : order.amount}
                                </span>

                                {!order.billed && (
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => {
                                            setIsEditingAmount(true);
                                            setIsEditing(false);   // ✅ reset rate editing
                                            setEditedRate(order.rate); // optional
                                        }}
                                    >
                                        Edit
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="d-flex gap-2">
                                <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    style={{ width: "110px" }}
                                    value={editedAmount}
                                    onChange={(e) => setEditedAmount(e.target.value)}
                                />

                                <button
                                    className="btn btn-sm btn-success"
                                    onClick={updateOrderDetails}
                                    disabled={saving}
                                >
                                    Save
                                </button>

                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => {
                                        setIsEditingAmount(false);
                                        setEditedAmount(order.amount);
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="d-flex justify-content-between pt-2">
                        <span className="text-muted small">Billed</span>
                        <span className={`fw-medium ${order.billed ? "text-success" : "text-secondary"}`}>
                            {order.billed ? "Yes" : "No"}
                        </span>
                    </div>

                </div>
            </div>

            {/* Description */}
            {order.description && (
                <div className="mb-4">
                    <div className="text-muted small mb-1">Description</div>
                    <div className="bg-light rounded-3 p-3 small">
                        {order.description}
                    </div>
                </div>
            )}

            {/* QR */}
            {/* <div className="text-center mb-4">
                <img
                    src={order.qrCodePath}
                    alt="qr"
                    className="img-fluid"
                    style={{ maxHeight: "140px" }}
                />
            </div> */}

            {/* Update Section */}
            {order.currentStage !== "Delivered" && (
                <div className="card border-0 shadow-sm rounded-4">
                    <div className="card-body p-3 p-sm-4">

                        <div className="text-muted small mb-2">
                            Update Status
                        </div>

                        <div className="d-flex flex-column flex-sm-row gap-2">

                            <div className="d-flex flex-wrap gap-2">

                                {stages.map((s, i) => (
                                    <button
                                        key={i}
                                        className={`btn ${stage === s
                                            ? "btn-dark"
                                            : "btn-outline-secondary"
                                            }`}
                                        style={{ borderRadius: "20px" }}
                                        onClick={() => setStage(s)}
                                    >
                                        {s}
                                    </button>
                                ))}

                            </div>

                            <button
                                className="btn btn-dark btn-lg w-100 mt-3"
                                onClick={updateStage}
                            >
                                Update
                            </button>

                        </div>

                    </div>
                </div>
            )}

            {isNotesModalOpen && (
                <div
                    className="modal d-block"
                    tabIndex="-1"
                    role="dialog"
                    style={{ backgroundColor: 'rgba(15, 23, 42, 0.55)' }}
                    onClick={() => setIsNotesModalOpen(false)}
                >
                    <div
                        className="modal-dialog modal-dialog-scrollable"
                        role="document"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="modal-content border-0 shadow">
                            <div className="modal-header">
                                <h5 className="modal-title">Order Notes</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setIsNotesModalOpen(false)}
                                    aria-label="Close"
                                ></button>
                            </div>
                            <div className="modal-body">
                                <label htmlFor="new-note" className="form-label small text-muted">
                                    Add a note
                                </label>
                                <textarea
                                    id="new-note"
                                    className="form-control mb-2"
                                    rows="3"
                                    placeholder="Enter note"
                                    value={noteInput}
                                    onChange={(event) => setNoteInput(event.target.value)}
                                />
                                <div className="d-flex justify-content-end mb-3">
                                    <button
                                        type="button"
                                        className="btn btn-dark btn-sm"
                                        onClick={handleAddNote}
                                        disabled={isAddingNote || !noteInput.trim()}
                                    >
                                        {isAddingNote ? 'Saving...' : 'Save Note'}
                                    </button>
                                </div>

                                {notesError && (
                                    <div className="alert alert-danger py-2" role="alert">
                                        {notesError}
                                    </div>
                                )}

                                {isLoadingNotes ? (
                                    <div className="text-center py-3">
                                        <div className="spinner-border spinner-border-sm"></div>
                                    </div>
                                ) : notes.length === 0 ? (
                                    <p className="text-muted small mb-0">No notes added yet.</p>
                                ) : (
                                    <div className="d-flex flex-column gap-2">
                                        {notes.map((item) => (
                                            <div key={item.id} className="border rounded-3 p-2 bg-light">
                                                <div className="small">{item.note}</div>
                                                <div className="text-muted" style={{ fontSize: '12px' }}>
                                                    {formatNoteTime(item.timeStamp)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => setIsNotesModalOpen(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

export default OrdersComponent;
